/** Host or path selects which lazy surface the single app mounts. */
export type AppSurface = 'docs' | 'admin' | 'crm' | 'compete' | 'settings' | 'learn' | 'get-hired';

const SURFACES: readonly AppSurface[] = [
  'docs',
  'admin',
  'crm',
  'compete',
  'settings',
  'learn',
  'get-hired',
];

declare global {
  interface Window {
    REASONED_APP_SURFACE?: string;
  }
}

function hintedSurface(): AppSurface | '' {
  if (typeof window === 'undefined') return '';
  const raw = String(window.REASONED_APP_SURFACE || '').trim().toLowerCase();
  if ((SURFACES as readonly string[]).includes(raw)) return raw as AppSurface;
  const attr = String(document.documentElement.getAttribute('data-app-surface') || '')
    .trim()
    .toLowerCase();
  if ((SURFACES as readonly string[]).includes(attr)) return attr as AppSurface;
  return '';
}

export function appSurface(
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
): AppSurface {
  const host = String(hostname || '').toLowerCase().split(':')[0];
  if (host === 'admin.reasonedfinance.com' || host.startsWith('admin.')) {
    return 'admin';
  }
  if (host === 'crm.reasonedfinance.com' || host.startsWith('crm.')) {
    return 'crm';
  }
  const path = String(pathname || '');
  if (path === '/compete' || path.startsWith('/compete/')) {
    return 'compete';
  }
  if (path === '/learn' || path.startsWith('/learn/')) {
    return 'learn';
  }
  if (path === '/get-hired' || path.startsWith('/get-hired/') || path === '/vault' || path.startsWith('/vault/')) {
    return 'get-hired';
  }
  if (path === '/settings' || path.startsWith('/settings/') || path === '/unsubscribe') {
    return 'settings';
  }
  // Shell hint (Go host) when the URL alone would default to Docs — covers path rewrites
  // and pairs with HTML cache-bust of main.js after Get Hired joined the single app.
  const hinted = hintedSurface();
  if (hinted) return hinted;
  return 'docs';
}

/** React Router basename for the active surface. Empty when the surface owns its own router. */
export function appBasename(surface = appSurface()): string {
  if (surface === 'compete') return '/compete';
  if (surface === 'learn') return '/learn';
  if (surface === 'get-hired') return '';
  return '';
}

/** Surfaces that mount their own createBrowserRouter (skip parent BrowserRouter). */
export function surfaceOwnsRouter(surface = appSurface()): boolean {
  return surface === 'get-hired';
}
